import type { BlockCards, CardItem } from 'types/block-cards';
import React from 'react';
import {
  Button,
  Paragraph,
  Wrap,
  ButtonLayoutProps,
} from '@leafwell/components';
import Image from 'next/image';
import Figure from './figure';
import { FigureProps } from './figure';
import HeadingBody from './heading-body';

const CardsBlockItem: React.FC<
  CardItem & {
    isColumn: boolean;
    isLast: boolean;
    isTextOnly: boolean;
    order: number;
    blockHasTitle: boolean;
    border?: boolean;
    columnGap?: string;
  }
> = ({
  button,
  buttonVariant = 'primary',
  description,
  descriptionTextColor,
  descriptionTextSize,
  isColumn,
  isLast,
  isTextOnly,
  order,
  image,
  imageAlignment,
  imageStyle,
  subtitle,
  title,
  blockHasTitle,
  icon,
  columnGap,
}) => {
  const hasSubTitle = subtitle?.length > 0;
  const offCenterGrid = isColumn && blockHasTitle;
  const hasIcon = icon !== null;
  const hasDescription = description?.length > 0;

  const itemClassNames = ['grid gap-3 md:gap-6'];

  if (isTextOnly) {
    itemClassNames.push('md:py-4');
    itemClassNames.push(
      order === 1 ? '' : 'md:border-l md:border-indigo-200 md:pl-10',
    );
    itemClassNames.push(isLast || hasIcon ? '' : 'md:pr-10');
  }

  if (isColumn && !isTextOnly) {
    if (columnGap !== 'large') {
      itemClassNames.push('gap-y-6 md:gap-x-10 md:items-center');
      itemClassNames.push(
        offCenterGrid
          ? 'md:grid-cols-6 xl:gap-x-12'
          : 'md:grid-cols-2 xl:gap-x-24',
      );
    } else {
      itemClassNames.push(
        'md:gap-x-10 md:items-start md:grid-cols-2 xl:gap-x-64',
      );
    }
  }

  const rightImage = imageAlignment === 'right';
  const centerImage = imageAlignment === 'center';
  const classNameFeatured = [];
  const classNameImage = ['object-top h-full'];

  classNameFeatured.push(offCenterGrid ? 'md:col-span-4' : '');
  classNameFeatured.push(
    imageStyle === 'cover'
      ? 'rounded-lg aspect-w-16 aspect-h-9'
      : 'h-full max-h-136',
  );

  if (isColumn) {
    classNameFeatured.push(
      rightImage
        ? 'md:order-last'
        : centerImage
        ? 'md:order-last justify-self-center'
        : '',
    );
  } else {
    classNameFeatured.push('md:aspect-w-1 md:aspect-h-1');
  }

  const imageClassNames = {
    className: classNameFeatured.join(' '),
    classNameImage: classNameImage.join(' '),
  };

  const cardGridClassNames = ['grid'];

  if (offCenterGrid) {
    cardGridClassNames.push('md:col-span-2');
  }

  if (isTextOnly) {
    cardGridClassNames.push('gap-x-3 md:gap-x-6 gap-y-6 md:gap-y-11');
    if (hasDescription) {
      cardGridClassNames.push('grid-rows-card-medium');
    }
    if (hasIcon) {
      cardGridClassNames.push('grid-rows-[3rem_auto]');
    }
  } else {
    cardGridClassNames.push('gap-3 md:gap-6');

    if (!isColumn) {
      cardGridClassNames.push('!gap-y-2 grid-rows-card-block');
    }
  }

  return (
    <article className={itemClassNames.join(' ')}>
      {!isTextOnly && image && (
        <Figure
          {...image}
          {...imageClassNames}
          objectFit={imageStyle as FigureProps['objectFit']}
        />
      )}
      <div className={cardGridClassNames.join(' ')}>
        {isColumn && hasSubTitle && (
          <Image
            src="/asterisk.svg"
            alt=""
            className="w-8"
            loading="lazy"
            width={32}
            height={32}
          />
        )}
        {(hasIcon || hasSubTitle) && (
          <div
            className={
              hasIcon ? 'flex justify-between items-center self-start' : ''
            }
          >
            {hasSubTitle && (
              <Paragraph
                size={isColumn || hasIcon ? 'large' : 'small'}
                tagName="h5"
                className="font-medium"
                dangerouslySetInnerHTML={{ __html: subtitle }}
              />
            )}
            {hasIcon && <Figure {...icon} />}
          </div>
        )}
        <HeadingBody
          level={!isColumn || offCenterGrid || hasSubTitle ? '4' : '2'}
          title={title}
          tagName={'h3'}
          className={hasIcon ? 'self-start font-semibold' : 'font-semibold'}
        />
        {hasDescription && (
          <Paragraph
            className={[
              descriptionTextColor === 'light' ? 'text-gray-600' : '',
              descriptionTextSize === 'large' ? 'text-lg' : '',
            ].join(' ')}
            size={isTextOnly ? 'large' : 'small'}
            dangerouslySetInnerHTML={{ __html: description }}
            tagName="div"
          />
        )}
        {button && (
          <Button
            className={[
              'justify-self-start self-end',
              !isColumn && !isTextOnly ? 'mt-4' : '',
            ].join(' ')}
            href={button.url}
            label={button.title}
            rounded={true}
            target={button.target}
            variant={buttonVariant as ButtonLayoutProps['variant']}
          />
        )}
      </div>
    </article>
  );
};

const CardsBlock: React.FC<BlockCards> = ({
  sectionId,
  direction,
  title,
  items,
  variant,
  columnGap,
}) => {
  const blockHasTitle = title?.length > 0;
  const isColumn = direction === 'column';
  const isTextOnly = variant === 'text' || variant === 'icon';

  return (
    <Wrap
      className={[
        'leafwell-cards-block',
        isTextOnly || isColumn ? 'md:grid-cols-3 gap-x-10 xl:gap-x-16' : '',
      ].join(' ')}
      id={sectionId}
    >
      {title && (
        <HeadingBody
          className="col-span-full font-semibold"
          level="2"
          title={title}
          tagName="h3"
        />
      )}
      {items.map((item, key) => (
        <CardsBlockItem
          {...item}
          key={`${sectionId}-${key}`}
          isColumn={!isColumn}
          isLast={key + 1 === items.length}
          order={key + 1}
          isTextOnly={isTextOnly}
          blockHasTitle={blockHasTitle}
          columnGap={columnGap}
        />
      ))}
    </Wrap>
  );
};

export default CardsBlock;
